import { groupBy, keyBy, mapValues } from 'lodash-es';
import { DateTime } from 'luxon';
import { MapActivity, MapWorkstream } from '../../ioStore';
import { calculateMaxWindowedActivitiesSumForProject } from './calculateMaxWindowedActivitiesSumForProject';

const MIN_WINDOW = 5;
const WINDOW_RATIO = 0.1;
const MIN_LANES_IN_WORKSTREAM = 2;

/**
 * Calculates the ideal number of Activity Lanes per Workstream
 * based on the Activities distribution throughout the project.
 *
 * @returns A dictionary of the ideal max number of Activity Lanes per Workstream
 */
export function calculateIdealMaxLanesPerWorkstreamInProject(
  workstreams: MapWorkstream[],
  activitiesInWorkstream: (wsId: string) => MapActivity[],
  projectStart: string,
  projectDuration: number,
  maxLanesPerWorkstream: number,
): Record<string, number> {
  const workstreamsById = keyBy(workstreams, 'id');
  const slidingWindow = Math.trunc(
    Math.max(projectDuration * WINDOW_RATIO, MIN_WINDOW),
  );

  /**
   * Calculates the ideal number of Activity Lanes for Workstream
   * in Project mode.
   */
  const getIdealMaxLanesForProjectWorkstream = (workstream: MapWorkstream) => {
    const activities = activitiesInWorkstream(workstream.id);
    const activitiesPerDay = groupBy(activities, (activity) =>
      DateTime.fromISO(activity.deadline).toISODate(),
    );

    const startDateStr = projectStart;

    /**
     * Calculate the max number of lanes required for the Workstream
     */
    const maxLanesRequiredForWorkstream =
      calculateMaxWindowedActivitiesSumForProject(
        startDateStr,
        projectDuration,
        activitiesPerDay,
        slidingWindow,
      );

    return Math.max(
      Math.min(maxLanesRequiredForWorkstream, maxLanesPerWorkstream),
      MIN_LANES_IN_WORKSTREAM,
    );
  };

  const idealMaxLanesPerWorkstream = mapValues(
    workstreamsById,
    getIdealMaxLanesForProjectWorkstream,
  );

  return idealMaxLanesPerWorkstream;
}
